import _ from 'underscore';
import moment from 'moment';
import d3 from 'd3';
import {bisector, min as d3_min, extent as d3_extent} from 'd3-array';
import {scaleLinear, scaleTime} from 'd3-scale';
import {area as d3_area, line as d3_line} from 'd3-shape';
import {timeDay} from 'd3-time';
import d3_lucid from 'common/charts/d3-lucid';
import {
  AnalyticAppEnum,
  AnalyticAppInteractionEnum,
  AnalyticAppRendererEnum,
  DateTimeFormat,
  ReportingPeriodEnum,
} from 'common/proxies/enum-proxy';
import * as AnalyticAppColor from 'common/constants/analytic-app-color';
import {
  BOTTOM,
  DEFAULT_DURATION,
  DELAY,
  LEFT,
  RIGHT,
  SHORT,
} from 'common/constants';
import {
  getDateFormat,
  getDateFormatVerbose,
  getEndOfPeriod,
  isDuplicateTickValue,
} from 'common/utils/date-util';
import {appendPx, getID} from 'common/utils/dom-util';
import {getClosest} from 'common/utils/number-util';
import {formatDate, formatDateRange} from 'common/formatters/date-formatter';
import {formatNumber} from 'common/formatters/number-formatter';
import BackArrow from 'common/svg/back-arrow.svg';
import ForwardArrow from 'common/svg/forward-arrow.svg';

const {currencyUnit} = window.settings.organization;

const timeseriesChart = function () {
  // X-Axis:
  // Multiple timeseries of different lengths may be returned
  // - Determine which series is longest
  // - Render the x axis ticks based on the longest series
  // - Always render 1 tick per value in series
  // - All series using the number of ticks as domain
  // - checks for duplicate tick values
  // - Clean function hides every other tick label if >45

  const ANIMATION_DURATION = 2000;
  const BACK = 'back';
  const FORWARD = 'forward';
  const NO_LABEL = '';
  let _svgDivId;
  let _svgId;

  function chart() {
    // States

    const states = {
      // Appends a background and a spinner:
      init(params) {
        _svgDivId = getSvgDivId(params.selection);
        _svgId = getSvgId(params.selection);
        params.svgContainer = appendSvgContainer(params.selection);
        params.svg = appendRoot(
          params.svgContainer,
          params.width,
          params.height,
        );
        appendBackgroundRect(params.svg, params.width, params.height);
        d3_lucid.svg.spinner(params.selection);
      },
      // Fades existing content and appends a spinner:
      loading(params) {
        d3_lucid.svg.removeByClassName(d3_lucid.classes.MESSAGE);
        d3_lucid.svg.spinner(params.selection);
        params.svg = d3_lucid.svg
          .selectByID(_svgId)
          .style('pointer-events', 'none');
        params.content = d3_lucid.svg.selectByID(d3_lucid.id.CONTENT);
        transition_fadeRect();
      },
      // Fades existing content (if any) and appends a message:
      message(params) {
        params.svg = d3_lucid.svg
          .selectByID(_svgId)
          .style('pointer-events', 'none');
        d3_lucid.svg.removeSpinner(params.selection);
        appendMessage(params);
        reenableArrowButtons(params);
      },
      // Appends a chart:
      normal(params) {
        params.series = getStretchedSeries(params);
        params.mainSeries = params.series[getIndexOfLongestSeries(params)];
        params.units = getUnits(params);
        params.margins = getMargins(params);
        params.padding = getPadding(params);
        params.contentWidth = getContentWidth(params);
        params.contentHeight = getContentHeight(params);
        params.columnWidthScale = getColumnWidthScale(params);
        params.columnWidth = getComputedColumnWidth(params);
        params.numReadings = getNumReadings(params.mainSeries.values);
        params.numColumnSeries = getNumColumnSeries(params);
        params.duration = ANIMATION_DURATION;
        params.rangeX = getRangeX(params);
        params.rangeY = getRangeY(params);
        params.series = getSeriesAttributes(params);
        params.axisX = getAxisX(params);
        params.axesY = getAxesY(params);
        params.isCustomPeriod = getCustomPeriodFlag(params);

        d3_lucid.svg.removeSpinner(params.selection);
        d3_lucid.svg.removeByID(_svgId);

        params.svgContainer = appendSvgContainer(params.selection);
        params.svg = appendRoot(
          params.svgContainer,
          params.width,
          params.height,
        );
        params.defs = d3_lucid.svg.defs(params.svg);

        appendBackgroundRect(params.svg, params.width, params.height);
        appendContentBackgroundRect(params);
        appendGridLines(params);

        params.content = appendContent(params);

        appendAxisX(params);
        appendAxesY(params);
        appendLegend(params);
        appendSeries(params);
        appendArrowButtons(params);
        appendPeriodLabel(params);
        appendFadeRect(params);

        playTransitions(params);
      },
    };

    // Set state

    states[state]({
      app,
      dispatcher,
      enableArrowButtons,
      height,
      message,
      page,
      period,
      selection,
      series,
      showCustomPeriodLabel,
      state,
      width,
    });

    // Functions

    function addAnimationToSeries_Area(params, timeseries) {
      const clipRect = timeseries.clipRect;
      const contentWidth = params.contentWidth;
      if (timeseries.animate) {
        const scaleX = timeseries.scaleX;
        timeseries.transition = function () {
          clipRect
            .transition()
            .attr('width', scaleX(timeseries.end))
            .duration(params.duration)
            .each('end', () => {
              clipRect.attr('width', contentWidth);
            });
        };
      } else {
        timeseries.transition = function () {
          clipRect.attr('width', contentWidth);
        };
      }
    }

    function addAnimationToSeries_Column(params, timeseries) {
      const column = timeseries.column;
      const contentHeight = params.contentHeight;
      const scaleY = timeseries.scaleY;
      if (timeseries.animate) {
        timeseries.transition = function () {
          column
            .transition()
            .duration(DEFAULT_DURATION)
            .delay((datum, ii) => {
              return DELAY * ii;
            })
            .ease(d3.ease('quad-out'))
            .attr('height', (datum) => {
              if (datum.value) {
                if (datum.value >= 0) {
                  return scaleY(0) - scaleY(datum.value);
                }
              }
              if (datum.value < 0) {
                return scaleY(datum.value) - scaleY(0);
              }
              return 0;
            })
            .attr('y', (datum) => {
              if (datum.value) {
                const zero = scaleY(0);
                if (datum.value >= 0) {
                  const posY = zero - scaleY(datum.value);
                  return zero - posY;
                }
                if (datum.value < 0) {
                  return zero;
                }
              }
              return 0;
            });
        };
      } else {
        timeseries.transition = function () {
          column
            .attr('height', (datum) => {
              if (datum.value) {
                if (datum.value >= 0) {
                  return scaleY(0) - scaleY(datum.value);
                }
              }
              if (datum.value < 0) {
                return scaleY(datum.value) - scaleY(0);
              }
              return 0;
            })
            .attr('y', (datum) => {
              if (
                timeseries.renderer.slug ===
                AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug
              ) {
                return scaleY(datum.highValue);
              }
              const posY = contentHeight - scaleY(datum.value);
              return contentHeight - posY;
            });
        };
      }
    }

    function addAnimationToSeries_Line(params, timeseries) {
      const clipRect = timeseries.clipRect;
      const contentWidth = params.contentWidth;
      if (timeseries.animate) {
        const scaleX = timeseries.scaleX;
        timeseries.transition = function () {
          clipRect
            .transition()
            .attr('width', scaleX(timeseries.end))
            .duration(params.duration)
            .each('end', () => {
              clipRect.attr('width', contentWidth);
            });
        };
      } else {
        timeseries.transition = function () {
          clipRect.attr('width', contentWidth);
        };
      }
    }

    function addAnimationToSeries_Plot(timeseries) {
      const circle = timeseries.circle;
      const scaleY = timeseries.scaleY;
      if (timeseries.animate) {
        timeseries.transition = function () {
          circle
            .transition()
            .duration(DEFAULT_DURATION)
            .delay((datum, ii) => {
              return 50 * ii;
            })
            .ease(d3.ease('quad-out'))
            .attr('cy', (datum) => {
              if (datum.value) {
                return scaleY(datum.value);
              }
            })
            .attr('opacity', 1);
        };
      } else {
        timeseries.transition = function () {
          circle
            .attr('cy', (datum) => {
              if (datum.value) {
                return scaleY(datum.value);
              }
            })
            .attr('opacity', 1);
        };
      }
    }

    function addInteractivityToSeries_Column(params, timeseries, index) {
      const interaction = timeseries.interaction;
      if (interaction !== AnalyticAppInteractionEnum.NONE) {
        const node = timeseries.elements.node();
        const columns = d3.selectAll(node.childNodes)[0];
        const content = params.content;
        const legendIcon = timeseries.legendIcon;
        const legendReadout = timeseries.legendReadout;
        let selectedPosition = -1;
        const values = timeseries.values;
        const colorPrimary = timeseries.stroke;
        const colorSecondary = timeseries.fill;

        const chartElement = params.selection[0][0];
        const x = getScaleXbyTicks(timeseries, chartElement);

        content.on(`mousemove.${index}`, function () {
          const mouse = d3.mouse(this);
          const position = getCorrespondingIndex(x, mouse);
          const datum = values[position];

          let color;
          if (datum) {
            color = datum.color;
            if (datum.color) {
              legendIcon.style('fill', color.primary);
              legendReadout.style('fill', color.primary);
            }

            let value = formatNumber(datum.value);
            if (timeseries.unit.id === currencyUnit.id) {
              if (value.charAt(0) === '-') {
                value = `-$${value.slice(1, value.length)}`;
              } else {
                value = `$${value}`;
              }
            } else {
              value = value ? [value, timeseries.unit.shortName].join(' ') : '';
            }

            legendReadout.text(value);
          } else {
            legendIcon.style('fill', colorPrimary);
            legendReadout.style('fill', colorPrimary);
            legendReadout.text('');
          }

          const column = columns[position];
          if (column) {
            d3.select(column)
              .attr('fill', () => {
                if (color) {
                  return color.secondary;
                }
                return colorSecondary;
              })
              .style('display', 'inherit');

            if (position !== selectedPosition) {
              const selectedDatum = values[selectedPosition];
              let previousColor;
              if (selectedDatum) {
                previousColor = selectedDatum.color;
              }
              d3.select(columns[selectedPosition])
                .attr('fill', () => {
                  if (previousColor) {
                    return previousColor.primary;
                  }
                  return colorPrimary;
                })
                .style('display', () => {
                  switch (timeseries.renderer.slug) {
                    case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
                      return 'none';
                    default:
                  }
                });
              selectedPosition = position;
            }
          }
        });

        content.on(`mouseout.${index}`, () => {
          for (let ii = 0, nn = columns.length; ii < nn; ii++) {
            const datum = timeseries.values[ii];
            d3.select(columns[ii])
              .attr('fill', () => {
                if (datum.color) {
                  return datum.color.primary;
                }
                return timeseries.stroke;
              })
              .style('display', () => {
                switch (timeseries.renderer.slug) {
                  case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
                    return 'none';
                  default:
                }
              });
          }
          legendReadout.text('');
        });
      }
    }

    function addInteractivityToSeries_Plot(params, timeseries, index) {
      const interaction = timeseries.interaction;
      if (interaction !== AnalyticAppInteractionEnum.NONE) {
        const content = params.content;
        const legendReadout = timeseries.legendReadout;
        const values = timeseries.values;

        const chartElement = params.selection[0][0];
        const x = getScaleXbyTicks(timeseries, chartElement);

        content.on(`mousemove.${index}`, function () {
          const mouse = d3.mouse(this);
          const correspondingIndex = getCorrespondingIndex(x, mouse);
          const datum = values[correspondingIndex];
          if (datum) {
            const color = datum.color;
            legendReadout.style('fill', color.primary);
            const value = formatNumber(datum.value);
            const readout = value
              ? [value, timeseries.unit.shortName].join(' ')
              : '';
            legendReadout.text(readout);
          } else {
            legendReadout.text('');
          }
        });

        content.on(`mouseout.${index}`, () => {
          legendReadout.text('');
        });
      }
    }

    function addMarkerListeners(params, series, index) {
      const points = series.points;
      const content = params.content;
      content.on(`mousemove.series${index}`, function () {
        const mouse = d3.mouse(this);
        const position = series.bisect(points, mouse[0]);
        const point = points[position];
        if (point) {
          updateLegendReadout(series, point);
          updateMarker(params, series, point, mouse);
        } else {
          updateLegendReadout(series);
          updateMarker(params, series);
        }
      });
      content.on(`mouseout.series${index}`, () => {
        updateLegendReadout(series);
        updateDynamicLegendItem(params);
        updateMarker(params, series);
      });
    }

    function appendArrowButtons(params) {
      if (params.enableArrowButtons && !params.isCustomPeriod) {
        const buttonHeight = 16;
        const buttonWidth = 18;
        const spacing = 6;
        const forwardButtonPosX = buttonWidth + spacing;
        const mouseOverOpacity = 0.6;
        let posX = 1118;
        if (params.axesY.length > 1) {
          posX -= params.margins.right;
        }
        const buttons = params.svg
          .append('g')
          .attr('id', d3_lucid.id.ARROW_BUTTONS)
          .attr('transform', `translate(${posX},10)`);
        const backButton = buttons
          .append('g')
          .attr('id', 'backButton')
          .style('cursor', 'pointer')
          .on('click', () => {
            const direction = BACK;
            scroll(params, direction);
          })
          .on('mouseout', function () {
            d3.select(this).style('opacity', 1);
          })
          .on('mouseover', function () {
            d3.select(this).style('opacity', mouseOverOpacity);
          });
        backButton
          .append('rect')
          .attr('fill', AnalyticAppColor.WHITE.primary)
          .attr('height', buttonHeight)
          .attr('opacity', 0)
          .attr('width', buttonWidth);
        const forwardButton = buttons
          .append('g')
          .attr('id', 'forwardButton')
          .attr('transform', `translate(${forwardButtonPosX},0)`)
          .style('cursor', 'pointer')
          .on('click', () => {
            const direction = FORWARD;
            scroll(params, direction);
          })
          .on('mouseout', function () {
            d3.select(this).style('opacity', 1);
          })
          .on('mouseover', function () {
            d3.select(this).style('opacity', mouseOverOpacity);
          });
        forwardButton
          .append('rect')
          .attr('fill', AnalyticAppColor.WHITE.primary)
          .attr('height', buttonHeight)
          .attr('opacity', 0)
          .attr('width', buttonWidth);
        $('#backButton').append(BackArrow);
        $('#forwardButton').append(ForwardArrow);

        // Disable forward button where forward means future:
        if (params.page < 2) {
          forwardButton
            .style('cursor', 'default')
            .style('opacity', 0.2)
            .style('pointer-events', 'none')
            .on('click', null)
            .on('mouseout', null)
            .on('mouseover', null);
        }
        if (params.page > 1) {
          const series = params.mainSeries;
          const period = params.period;
          const periodLabel = getArrowButtonsLabel(series, period);
          buttons
            .append('text')
            .attr('id', d3_lucid.id.ARROW_BUTTONS_LABEL)
            .attr('x', -10)
            .attr('y', buttonHeight * 0.5 + 1)
            .style('dominant-baseline', 'central')
            .style('text-anchor', 'end')
            .text(periodLabel);
        }
      }
    }

    function appendBackgroundRect(selection, width, height) {
      return d3_lucid.svg.rect(selection, [
        {
          class: d3_lucid.classes.BACKGROUND_RECT,
          height,
          width,
        },
      ]);
    }

    function appendFadeRect(params) {
      params.svg
        .append('rect')
        .attr('class', d3_lucid.classes.FADE_RECT)
        .attr('fill-opacity', 0)
        .attr('height', params.contentHeight)
        .attr('id', d3_lucid.id.FADE_RECT)
        .attr('width', params.contentWidth)
        .attr('x', params.margins.left);
    }

    function appendAxisX(params) {
      const posX = params.margins.left;
      const posY = params.contentHeight;

      params.svg
        .append('g')
        .attr('class', 'x axis')
        .attr('transform', `translate(${posX},${posY})`)
        .call(params.axisX)
        .selectAll('.tick')
        .style('opacity', function () {
          // Hide ticks if no label
          const text = this.querySelector('text').textContent;
          if (text === NO_LABEL) {
            return '0';
          }
        });

      const offset = 2;

      const tooltipY = params.contentHeight + offset;

      const tooltipHeight = params.margins.bottom - offset;

      const tooltip = params.svg
        .append('g')
        .attr('id', 'xAxisTooltip')
        .style('display', 'none')
        .style('pointer-events', 'none');

      const background = tooltip
        .append('rect')
        .attr('fill', AnalyticAppColor.BLACK.primary)
        .attr('height', tooltipHeight)
        .attr('opacity', 0.75);

      const dateText = tooltip
        .append('text')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '18px')
        .attr('font-weight', 'bold')
        .attr('height', tooltipHeight)
        .attr('text-anchor', 'middle')
        .attr('y', tooltipHeight * 0.5 + 1)
        .style('fill', AnalyticAppColor.WHITE.primary);

      const left = params.margins.left;
      const timeseries = params.mainSeries;
      const tooltipOffset = getTooltipOffset(params, timeseries);
      const dateFormat = getDateFormatVerbose(params.period);
      const padding = 16;

      params.svg.on('mousemove.axis-tooltip', function () {
        const mouse = d3.mouse(this);
        const mouseX = mouse[0];
        if (mouseX > left) {
          const date = timeseries.scaleX.invert(mouseX - tooltipOffset);
          let tooltipText = formatDate(date, dateFormat);

          // For Load Profile and proximity interaction,
          // the axis tooltip should reflect the date
          // of the series that the user is currently
          // interacting with.
          if (params.dynamicStartDate) {
            const dynamicDate = moment(date)
              .date(params.dynamicStartDate.date())
              .month(params.dynamicStartDate.month())
              .year(params.dynamicStartDate.year());
            tooltipText = formatDate(dynamicDate, dateFormat);
          }
          dateText.text(tooltipText);

          const dateWidth = dateText.node().getComputedTextLength();
          const backgroundWidth = dateWidth + padding;
          const halfWidth = backgroundWidth * 0.5;
          let tooltipX = mouseX;
          const max = params.width - halfWidth;
          const min = halfWidth;
          if (tooltipX > max) {
            tooltipX = max;
          }
          if (tooltipX < min) {
            tooltipX = min;
          }
          background.attr('width', backgroundWidth).attr('x', -halfWidth);
          tooltip
            .attr('transform', `translate(${tooltipX},${tooltipY})`)
            .style('display', 'inherit');
        } else {
          tooltip.style('display', 'none');
        }
      });
      params.svg.on('mouseout.axis-tooltip', () => {
        tooltip.style('display', 'none');
      });
      params.xAxisTooltip = tooltip;

      const element = params.selection[0][0];
      cleanAxisX(element);
    }

    function cleanAxisX(chartElement) {
      // Render tick labels based on how many will fit
      // in the x axis

      // Find out how many tick labels
      const labeledTicks = d3
        .select(chartElement)
        .selectAll('.x .tick')[0]
        .filter((node) => node.querySelector('text').textContent);
      const numTickLabels = labeledTicks.length;

      // Determine number that will fit into x axis comfortably
      // This should be the width of the chart / width of the label
      const chart = chartElement.querySelectorAll('#contentContainer')[0];
      const chartWidth = chart && chart.getBBox().width;

      const firstLabeledTick = labeledTicks[0];
      const labeledTickWidth =
        firstLabeledTick && firstLabeledTick.getBBox().width;
      // Ensure howManyWillFit is not 0, as it is used as a divisor later
      // If it is 0, fall back to 30 ticks, as that is a low, safe number
      const howManyWillFit = labeledTickWidth
        ? Math.floor(chartWidth / labeledTickWidth)
        : 30;

      // If more tick labels than will fit...
      if (numTickLabels > howManyWillFit) {
        // Divide number of ticks by how many will fit,
        // Rounding up, to get a group of ticks where only 1
        // Will have a label
        const numTicksPerLabel = Math.ceil(numTickLabels / howManyWillFit);

        // Render 1 label for every numTicksPerLabel
        labeledTicks.forEach((tickNode, tickIndex) => {
          const showNext = tickIndex % numTicksPerLabel === 0;
          if (!showNext) {
            tickNode.querySelector('text').textContent = NO_LABEL;
          }
        });
      }
    }

    function appendAxesY(params) {
      const axesY = params.axesY;
      const left = params.margins.left;
      const top = params.padding.top;
      const contentHeight = params.contentHeight;
      const posX = (contentHeight + top) * 0.5;
      const posY = 18;
      // This line was causing issues with overlap on the right Y Axis on the /meters graph
      // For (const ii = 0, nn = axesY.length; ii < nn; ii++) {
      // Replacement:
      for (let ii = 0, nn = 2; ii < nn; ii++) {
        const axisY = axesY[ii];
        if (axisY) {
          const axis = params.svg
            .append('g')
            .attr('class', d3_lucid.classes.Y_AXIS)
            .call(axisY);
          if (ii === 0) {
            axis
              .attr('transform', `translate(${left},0)`)
              .append('text')
              .attr('class', d3_lucid.id.Y_AXIS_LABEL)
              .attr('fill', AnalyticAppColor.BLACK.primary)
              .attr('font-weight', 'bold')
              .attr('text-anchor', 'middle')
              .attr('transform', 'rotate(-90)')
              .attr('x', appendPx(-posX))
              .attr('y', appendPx(posY))
              .text(axisY.displayName);
          } else {
            const axisPosX = params.width - params.margins.right;
            axis
              .attr('transform', `translate(${axisPosX},0)`)
              .append('text')
              .attr('class', d3_lucid.id.Y_AXIS_LABEL)
              .attr('fill', AnalyticAppColor.BLACK.primary)
              .attr('font-weight', 'bold')
              .attr('text-anchor', 'middle')
              .attr('transform', 'rotate(90)')
              .attr('x', appendPx(posX))
              .attr('y', appendPx(posY))
              .text(axisY.displayName);
          }
        }
      }
    }

    function appendCaps(series) {
      const path = series.path.node();
      const start = path.getPointAtLength(0);
      const end = path.getPointAtLength(path.getTotalLength());
      series.elements
        .append('circle')
        .attr('class', 'marker start')
        .attr('cx', start.x)
        .attr('cy', start.y)
        .attr('fill', series.stroke)
        .attr('r', 4)
        .attr('stroke', series.stroke)
        .attr('stroke-width', series.strokeWidth);
      series.elements
        .append('circle')
        .attr('class', 'marker end')
        .attr('cx', end.x)
        .attr('cy', end.y)
        .attr('fill', series.stroke)
        .attr('r', 4)
        .attr('stroke', series.stroke)
        .attr('stroke-width', series.strokeWidth);
    }

    function appendContent(params) {
      const posX = params.margins.left;
      const clipPathID = d3_lucid.id.CONTENT_CLIP_PATH;
      const contentHeight = params.contentHeight;
      const contentWidth = params.contentWidth;
      params.defs
        .append('clipPath')
        .attr('id', clipPathID)
        .append('rect')
        .attr('height', contentHeight)
        .attr('width', contentWidth);
      const group = params.svg
        .append('g')
        .attr('clip-path', `url(#${clipPathID})`)
        .attr('id', 'contentClipGroup')
        .attr('transform', `translate(${posX},0)`);
      const content = group.append('g').attr('id', d3_lucid.id.CONTENT);
      content
        .append('rect')
        .attr('fill', '#FF00CC')
        .attr('height', contentHeight)
        .attr('width', contentWidth)
        .style('opacity', 0);
      return content;
    }

    function appendContentBackgroundRect(params) {
      return params.svg
        .append('rect')
        .attr('class', d3_lucid.classes.CONTENT_BACKGROUND_RECT)
        .attr('filter', `url(#${d3_lucid.id.SHADOW})`)
        .attr('height', params.contentHeight)
        .attr('transform', `translate(${params.margins.left},0)`)
        .attr('width', params.contentWidth);
    }

    function appendGridLines(params) {
      const primaryAxisY = params.axesY[0];
      const left = params.margins.left;
      const className = d3_lucid.classes.BACKGROUND_GRID_LINES;
      const id = d3_lucid.id.BACKGROUND_GRID_LINES;
      return params.svg
        .append('g')
        .attr('class', className)
        .attr('id', id)
        .attr('transform', `translate(${left},0)`)
        .call(primaryAxisY);
    }

    function appendLegend(params) {
      const legendSeries = getLegendSeries(params.series);
      const legendItemWidths = [];
      const legendItemPositions = [];
      const legendPosX = params.margins.left + 12;
      const spacing = 18;
      const legend = params.svg
        .append('g')
        .attr('id', d3_lucid.id.LEGEND)
        .attr('transform', `translate(${legendPosX},20)`);
      legend
        .selectAll()
        .data(legendSeries)
        .enter()
        .append('g')
        .each(function (series, index) {
          const legendItem = d3.select(this);
          appendLegendIcon(legendItem, series);
          appendLegendDisplayName(legendItem, series);
          appendLegendReadout(legendItem, series);
          legendItemWidths[index] = legendItem.node().getBBox().width;
          const prevWidth = legendItemWidths[index - 1];
          const prevX = legendItemPositions[index - 1];
          if (prevWidth) {
            const posX = prevX + prevWidth + spacing;
            legendItemPositions[index] = posX;
            legendItem.attr('transform', `translate(${posX},0)`);
          } else {
            legendItemPositions[index] = 0;
          }
          series.legendItem = legendItem;

          // We created a dynamic legend item for proximity series,
          // and now we have to dissociate it from any one particular
          // series so that it can be reused by any given series.
          if (series.interaction === AnalyticAppInteractionEnum.PROXIMITY) {
            series.legendDisplayName.text('');
            series.legendItem.style('display', 'none');
            params.dynamicLegendDisplayName = series.legendDisplayName;
            params.dynamicLegendIcon = series.legendIcon;
            params.dynamicLegendItem = series.legendItem;
            params.dynamicLegendReadout = series.legendReadout;
            delete series.legendDisplayName;
            delete series.legendIcon;
            delete series.legendItem;
            delete series.legendReadout;
          }
        });
    }

    function appendLegendDisplayName(legendItem, series) {
      series.legendDisplayName = legendItem
        .append('text')
        .attr('font-size', appendPx(14))
        .attr('x', (series) => {
          return series.iconWidth + 6;
        })
        .attr('y', 4)
        .text((series) => {
          return series.displayName;
        });
    }

    function appendLegendIcon(legendItem, series) {
      switch (series.renderer.slug) {
        case AnalyticAppRendererEnum.AREA.slug:
          appendLegendIcon_Line(legendItem, series);
          break;
        case AnalyticAppRendererEnum.CLUSTERED_COLUMN.slug:
          appendLegendIcon_Column(legendItem, series);
          break;
        case AnalyticAppRendererEnum.COLUMN.slug:
          appendLegendIcon_Column(legendItem, series);
          break;
        case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
          appendLegendIcon_Column(legendItem, series);
          break;
        case AnalyticAppRendererEnum.LINE.slug:
          appendLegendIcon_Line(legendItem, series);
          break;
        case AnalyticAppRendererEnum.PLOT.slug:
          appendLegendIcon_Plot(legendItem, series);
          break;
        default:
          // Do nothing
          break;
      }
    }

    function appendLegendIcon_Column(legendItem, series) {
      series.iconHeight = 14;
      series.iconWidth = getLegendIconWidth(series.renderer);
      series.legendIcon = legendItem
        .append('rect')
        .attr('fill', series.stroke)
        .attr('height', series.iconHeight)
        .attr('width', series.iconWidth)
        .attr('y', -(series.iconHeight * 0.5));
    }

    function appendLegendIcon_Line(legendItem, series) {
      series.iconWidth = getLegendIconWidth(series.renderer);
      const group = legendItem.append('g').style('stroke', series.stroke);
      const line = group
        .append('line')
        .attr('class', series.displayName)
        .attr('stroke-width', series.strokeWidth)
        .attr('x1', 0)
        .attr('x2', series.iconWidth)
        .attr('y1', 0)
        .attr('y2', 0);
      const strokeStyle = series.strokeStyle;
      if (strokeStyle === 'dashed') {
        line
          .attr('stroke-dasharray', '6,6')
          .attr('stroke-linecap', 'round')
          .attr('stroke-width', 3);
      }
      if (!series.hideMarker) {
        group
          .append('circle')
          .attr('cx', series.iconWidth * 0.5)
          .attr('cy', 0)
          .attr('fill', AnalyticAppColor.WHITE.primary)
          .attr('r', 7)
          .attr('stroke-width', series.strokeWidth);
      }
      series.legendIcon = group;
    }

    function appendLegendIcon_Plot(legendItem, series) {
      series.iconWidth = getLegendIconWidth(series.renderer);
      legendItem
        .append('circle')
        .attr('cx', series.iconWidth * 0.5)
        .attr('cy', 0)
        .attr('fill', '#CCC')
        .attr('r', 7)
        .attr('stroke', AnalyticAppColor.WHITE.primary)
        .attr('stroke-width', series.strokeWidth);
    }

    function appendLegendReadout(legendItem, series) {
      series.legendReadout = legendItem
        .append('text')
        .style('fill', (series) => {
          return series.stroke;
        })
        .attr('font-size', appendPx(12))
        .attr('y', 26);
    }

    function appendMarker(params, series, index) {
      const interaction = series.interaction;
      if (interaction !== AnalyticAppInteractionEnum.NONE) {
        series.marker = series.elements
          .append('circle')
          .attr('class', 'marker')
          .attr('id', `marker${index}`)
          .attr('display', 'none')
          .attr('fill', AnalyticAppColor.WHITE.primary)
          .attr('r', 7)
          .attr('stroke', series.stroke)
          .attr('stroke-width', series.strokeWidth)
          .attr('visibility', () => {
            if (series.hideMarker) {
              return 'hidden';
            }
            return 'visible';
          });
        series.bisect = getSeriesBisector();
        series.points = getSeriesPoints(series);
        addMarkerListeners(params, series, index);
      }
    }

    function appendMessage(params) {
      const center = d3_lucid.getCenter(params.width, params.height);
      return d3_lucid.svg
        .text(params.svg, [
          {
            class: d3_lucid.classes.MESSAGE,
            x: center.x,
            y: center.y,
          },
        ])
        .text(params.message);
    }

    function appendPeriodLabel(params) {
      if (params.isCustomPeriod && params.showCustomPeriodLabel) {
        const series = params.mainSeries;
        const periodLabel = getPeriodLabel(series);
        params.content
          .append('text')
          .attr('id', 'periodLabel')
          .attr('x', params.contentWidth - 12)
          .attr('y', 18)
          .style('dominant-baseline', 'central')
          .style('text-anchor', 'end')
          .text(periodLabel);
      }
    }

    function appendRoot(selection, width, height) {
      return d3_lucid.svg.root(selection, [
        {
          height,
          id: _svgId,
          width,
        },
      ]);
    }

    function appendSeries(params) {
      const series = params.series;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        switch (timeseries.renderer.slug) {
          case AnalyticAppRendererEnum.AREA.slug:
            appendSeries_Area(params, timeseries, ii);
            break;
          case AnalyticAppRendererEnum.CLUSTERED_COLUMN.slug:
            appendSeries_Column(params, timeseries, ii);
            break;
          case AnalyticAppRendererEnum.COLUMN.slug:
            appendSeries_Column(params, timeseries, ii);
            break;
          case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
            appendSeries_Column(params, timeseries, ii);
            break;
          case AnalyticAppRendererEnum.LINE.slug:
            appendSeries_Line(params, timeseries, ii);
            break;
          case AnalyticAppRendererEnum.PLOT.slug:
            appendSeries_Plot(params, timeseries, ii);
            break;
          default:
            // Do nothing
            break;
        }
      }
    }

    function appendSeries_Area(params, timeseries, index) {
      const contentHeight = params.contentHeight;
      const fill = timeseries.fill;
      const gridLineStroke = timeseries.gridLineStroke;
      const gridLineStrokeOpacity = timeseries.gridLineStrokeOpacity;
      const interpolate = timeseries.interpolate;
      const primaryAxisY = params.axesY[0];
      const scaleX = getScaleXbyTicks(timeseries, params.selection[0][0]);
      const scaleY = timeseries.scaleY;
      const stroke = timeseries.stroke;
      const strokeStyle = timeseries.strokeStyle;
      const strokeWidth = timeseries.strokeWidth;
      const values = timeseries.values;

      const area = d3_area()
        .defined((datum) => {
          if (datum.value || datum.value === 0) {
            return datum;
          }
        })
        .curve(interpolate)
        .x((datum, index) => {
          return scaleX(index);
        })
        .y0(contentHeight)
        .y1((datum) => {
          return scaleY(datum.value);
        });

      const line = d3_line()
        .defined((datum) => {
          if (datum.value || datum.value === 0) {
            return datum;
          }
        })
        .curve(interpolate)
        .x((datum, index) => {
          return scaleX(index);
        })
        .y((datum) => {
          return scaleY(datum.value);
        });

      const group = params.content
        .insert('g', timeseries.insert)
        .attr('id', timeseries.displayName);

      const clipRect = params.defs
        .append('clipPath')
        .attr('id', `seriesClipPath${index}`)
        .append('rect')
        .attr('height', contentHeight)
        .attr('id', `clipRect${index}`)
        .attr('width', 0);

      const elements = group
        .append('g')
        .attr('clip-path', `url(#seriesClipPath${index})`);

      elements.append('path').attr('d', area(values)).attr('fill', fill);

      elements
        .append('clipPath')
        .attr('id', `areaClipPath${index}`)
        .append('path')
        .attr('d', area(values))
        .attr('fill', fill);

      elements
        .append('g')
        .attr('class', d3_lucid.classes.AREA_GRID_LINES)
        .attr('clip-path', `url(#areaClipPath${index})`)
        .attr('stroke', gridLineStroke)
        .attr('opacity', gridLineStrokeOpacity)
        .call(primaryAxisY);

      const path = elements
        .append('path')
        .attr('d', line(values))
        .attr('fill', 'none')
        .attr('stroke', stroke)
        .attr('stroke-width', strokeWidth);

      if (strokeStyle === 'dashed') {
        path
          .attr('stroke-dasharray', '6,6')
          .attr('stroke-linecap', 'round')
          .attr('stroke-width', 3);
      }

      timeseries.clipRect = clipRect;
      timeseries.elements = elements;
      timeseries.path = path;
      addAnimationToSeries_Area(params, timeseries, index);
      appendMarker(params, timeseries, index);
    }

    function appendSeries_Column(params, timeseries, index) {
      const contentHeight = params.contentHeight;
      const contentWidth = params.contentWidth;
      const columnSpacing = 2;
      const columnWidth = params.columnWidth;
      const numColumnSeries = params.numColumnSeries;
      const scaleY = timeseries.scaleY;
      const scaleX = getScaleXbyTicks(timeseries, params.selection[0][0]);
      const maxWidth = 400;
      let extraLeftPadding = 0;

      params.defs
        .append('clipPath')
        .attr('id', `seriesClipPath${index}`)
        .append('rect')
        .attr('height', contentHeight)
        .attr('id', `clipRect${index}`)
        .attr('width', contentWidth);

      timeseries.elements = params.content
        .append('g')
        .attr('clip-path', `url(#seriesClipPath${index})`)
        .attr('id', timeseries.displayName);

      timeseries.column = timeseries.elements
        .selectAll('.column')
        .data(timeseries.values)
        .enter()
        .append('rect')
        .attr('class', 'column')
        .attr('fill', (datum) => {
          if (datum.color) {
            return datum.color.primary;
          }
          return timeseries.stroke;
        })
        .attr('height', 0)
        .attr('width', () => {
          const minWidth = 1;
          const width = Math.ceil(
            columnWidth / numColumnSeries - columnSpacing,
          );
          if (width > maxWidth) {
            extraLeftPadding = width - maxWidth;
            return maxWidth;
          } else if (width >= minWidth) {
            return width;
          }
          return minWidth;
        })
        .attr('x', (datum, index) => {
          return scaleX(index) - columnWidth * 0.5 + extraLeftPadding * 0.5;
        })
        .attr('y', scaleY(0))
        .style('display', () => {
          switch (timeseries.renderer.slug) {
            case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
              return 'none';
            default:
          }
        });
      addInteractivityToSeries_Column(params, timeseries, index);
      addAnimationToSeries_Column(params, timeseries, index);
    }

    function appendSeries_Line(params, timeseries, index) {
      const contentHeight = params.contentHeight;
      const interpolate = timeseries.interpolate;
      const scaleX = getScaleXbyTicks(timeseries, params.selection[0][0]);
      const scaleY = timeseries.scaleY;
      const stroke = timeseries.stroke;
      const strokeStyle = timeseries.strokeStyle;
      const strokeWidth = timeseries.strokeWidth;
      const values = timeseries.values;

      const line = d3_line()
        .defined((datum) => {
          if (datum.value || datum.value === 0) {
            return datum;
          }
        })
        .curve(interpolate)
        .x((datum, index) => {
          return scaleX(index);
        })
        .y((datum) => {
          return scaleY(datum.value);
        });

      const group = params.content
        .insert('g', timeseries.insert)
        .attr('id', timeseries.displayName.replace(' ', '_'));
      timeseries.clipRect = params.defs
        .append('clipPath')
        .attr('id', `seriesClipPath${index}`)
        .append('rect')
        .attr('height', contentHeight)
        .attr('id', `clipRect${index}`)
        .attr('width', 0);
      timeseries.elements = group
        .append('g')
        .attr('clip-path', `url(#seriesClipPath${index})`);

      if (stroke) {
        timeseries.path = timeseries.elements
          .append('path')
          .attr('d', line(values))
          .attr('fill', 'none')
          .attr('stroke', stroke)
          .attr('stroke-width', strokeWidth);
        if (strokeStyle === 'dashed') {
          timeseries.path
            .attr('stroke-dasharray', '6,6')
            .attr('stroke-linecap', 'round')
            .attr('stroke-width', 3);
        }
      }

      if (timeseries.capped) {
        appendCaps(timeseries);
      }
      addAnimationToSeries_Line(params, timeseries, index);
      appendMarker(params, timeseries, index);
    }

    function appendSeries_Plot(params, timeseries, index) {
      const contentHeight = params.contentHeight;
      const contentWidth = params.contentWidth;
      const scaleY = timeseries.scaleY;
      const scaleX = getScaleXbyTicks(timeseries, params.selection[0][0]);

      params.content
        .insert('g', timeseries.insert)
        .attr('id', timeseries.displayName);

      params.defs
        .append('clipPath')
        .attr('id', `seriesClipPath${index}`)
        .append('rect')
        .attr('height', contentHeight)
        .attr('id', `clipRect${index}`)
        .attr('width', contentWidth);

      const elements = params.content
        .append('g')
        .attr('clip-path', `url(#seriesClipPath${index})`)
        .attr('id', timeseries.displayName);

      timeseries.circle = elements
        .selectAll('.circle')
        .data(timeseries.values)
        .enter()
        .append('circle')
        .attr('class', 'plot-point')
        .attr('cx', (datum, index) => {
          return scaleX(index);
        })
        .attr('cy', (datum) => {
          return scaleY(datum.value) + 40;
        })
        .attr('fill', (datum) => {
          return datum.color.primary;
        })
        .attr('opacity', 0)
        .attr('r', 7)
        .attr('stroke', AnalyticAppColor.WHITE.primary)
        .attr('stroke-width', 2)
        .style('display', (datum) => {
          if (!datum.value) {
            return 'none';
          }
        });

      addInteractivityToSeries_Plot(params, timeseries, index);
      addAnimationToSeries_Plot(timeseries);
    }

    function appendSvgContainer(selection) {
      return d3_lucid.svg.div(selection, [
        {
          id: _svgDivId,
        },
      ]);
    }

    function getArrowButtonsLabel(series, period) {
      let format;
      switch (period.slug) {
        case ReportingPeriodEnum.WEEK.slug:
        case ReportingPeriodEnum.LAST_WEEK.slug:
          format = DateTimeFormat.TEXTUAL;
          break;
        case ReportingPeriodEnum.MONTH.slug:
        case ReportingPeriodEnum.LAST_MONTH.slug:
          format = DateTimeFormat.DATE;
          break;
        case ReportingPeriodEnum.YEAR.slug:
        case ReportingPeriodEnum.LAST_YEAR.slug:
          format = DateTimeFormat.TEXTUAL;
          break;
        default:
          format = DateTimeFormat.TEXTUAL_DAY_OF_WEEK;
          break;
      }
      return formatDateRange(series.start, series.end, format);
    }

    function getAxesY(params) {
      const units = params.units;
      const axesY = [];
      for (const propName in units) {
        const unit = units[propName];
        const timeseries = unit.series[0];
        const scaleY = timeseries.scaleY;
        const orient = getAxisOrientY(unit.index);
        const axis = d3.svg
          .axis()
          .orient(orient)
          .scale(scaleY)
          .tickFormat((datum) => {
            // Axes require abbreviation starting at 1000.
            return formatNumber(
              datum,
              'number',
              datum >= 1000
                ? {
                    average: true,
                    mantissa: 2,
                    thousandSeparated: true,
                    trimMantissa: true,
                  }
                : null,
            );
          })
          .ticks(5)
          .tickPadding(6)
          .tickSize(-params.contentWidth);
        axis.displayName = unit.type.displayName;
        axesY[unit.index] = axis;
      }
      return axesY;
    }

    function getAxisOrientY(index) {
      if (index > 0) {
        return RIGHT;
      }
      return LEFT;
    }

    function getAxisX(params) {
      const series = params.mainSeries;
      const scaleX = series.scaleX;
      const period = params.period;

      const tickLabels = {};

      // Var name "tickFormat" breaks, probably due to being a d3 method name
      const formatForTick = getDateFormat(period);

      const numTicks = params.numReadings;

      return d3.svg
        .axis()
        .orient(BOTTOM)
        .scale(scaleX)
        .tickFormat((tickValue) => {
          const formatted = formatDate(tickValue, formatForTick);

          // At high resolutions, many ticks will have the same tick label
          // If tick label is duplicate, just return empty string

          // If the tick label matches an existing tick label, check for duplicate
          if (tickLabels.hasOwnProperty(formatted)) {
            const existingTickValue = tickLabels[formatted];
            const tickIsDuplicate = isDuplicateTickValue(
              existingTickValue,
              tickValue,
              formatForTick,
            );
            if (tickIsDuplicate) {
              return NO_LABEL;
            }
          }

          // Otherwise, store the label/date
          tickLabels[formatted] = tickValue;

          return formatted;
        })
        .tickPadding(8)
        .ticks(numTicks);
    }

    function getColumnWidthScale(params) {
      const {mainSeries} = params;
      const unusedWidth =
        params.padding.left +
        params.padding.right +
        params.margins.right +
        params.margins.left;

      return scaleTime()
        .domain(getDomainX(mainSeries.start, mainSeries.end, period))
        .range([0, width - unusedWidth]);
    }

    function getCompacted(timeseries) {
      if (timeseries.compact) {
        const values = timeseries.values;
        const compacted = [];
        for (let jj = 0, mm = values.length; jj < mm; jj++) {
          const reading = values[jj];
          if (reading.value || reading.value === 0) {
            compacted.push(reading);
          }
        }
        timeseries.values = compacted;
      }
      return timeseries;
    }

    function getComputedColumnWidth(params) {
      const series = params.mainSeries;
      const values = series.values;
      const scale = params.columnWidthScale;
      const widths = [];
      let width = params.contentWidth / getNumReadings(values);

      for (let ii = values.length - 1; ii > 0; ii--) {
        widths.push(
          Math.round(
            Math.abs(
              scale(values[ii].timestamp) - scale(values[ii - 1].timestamp),
            ),
          ),
        );
      }
      width = d3_min(widths);

      return 0.75 * width;
    }

    function getConformed(params, series) {
      // If the selected period is month,
      // And there is a previous period
      // Comparison, the two months won't line
      // Up if they're not of equal durations.
      // Here we check for that case, and
      // Conform the comparison period's
      // Domain and scale to those of the
      // Main period.
      const period = params.period;
      const month = ReportingPeriodEnum.MONTH.slug;
      if (period.slug === month && series.length > 1) {
        const series0 = series[0];
        const domain0 = series0.domainX;
        const days0 = moment(domain0[0]).daysInMonth();
        const series1 = series[1];
        const domain1 = series1.domainX;
        const days1 = moment(domain1[0]).daysInMonth();
        if (days1 !== days0) {
          const end1 = domain1[1];
          const difference = days0 - days1;
          const adjustedEnd = timeDay.offset(end1, difference);
          const range1 = series1.rangeX;
          domain1[1] = adjustedEnd;
          series1.scaleX = getScaleX(domain1, range1);
          series1.values = series1.values.slice(0, days0);
        }
      }
      return series;
    }

    function getContentHeight(params) {
      const top = params.margins.top;
      const bottom = params.margins.bottom;
      return params.height - top - bottom;
    }

    function getContentWidth(params) {
      const left = params.margins.left;
      const right = params.margins.right;
      return params.width - left - right;
    }

    function getCustomPeriodFlag(params) {
      switch (params.period.slug) {
        case ReportingPeriodEnum.LESS_THAN_FOURTY_EIGHT_HOURS.slug:
        case ReportingPeriodEnum.LESS_THAN_SEVEN_DAYS.slug:
        case ReportingPeriodEnum.LESS_THAN_THIRTY_DAYS.slug:
        case ReportingPeriodEnum.LESS_THAN_NINETY_DAYS.slug:
        case ReportingPeriodEnum.LESS_THAN_TWELVE_MONTHS.slug:
        case ReportingPeriodEnum.MORE_THAN_TWELVE_MONTHS.slug:
          return true;
        default:
          return false;
      }
    }

    function getDomainX(start, end, period, values = []) {
      let endOfPeriod = end || getEndOfPeriod(start, period);

      // Grab the timestamp for the first item and default to that.
      // This is to prevent an edge case bug where the start timestamp
      // in the report metadata is earlier than the first data item.
      const {timestamp: startTimestamp = null} =
        values.find((element, index) => {
          return index === 0;
        }) || {};
      let startOfPeriod;

      switch (period.slug) {
        case ReportingPeriodEnum.LESS_THAN_TWELVE_MONTHS.slug:
        case ReportingPeriodEnum.MORE_THAN_TWELVE_MONTHS.slug:
          startOfPeriod = moment(startTimestamp || start)
            .startOf('month')
            .toDate();
          endOfPeriod = moment(end).endOf('month').toDate();
          break;
        default:
          startOfPeriod = moment(startTimestamp || start);
          break;
      }
      return [startOfPeriod, endOfPeriod];
    }

    function getDomainY(series) {
      let max = Number.MIN_VALUE;
      let min = Number.MAX_VALUE;
      let clampToZero = true;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        if (timeseries.clampToZero === false) {
          clampToZero = false;
        }
        const values = timeseries.values;
        const extent = d3_extent(values, (datum) => {
          return datum.value;
        });
        if (
          timeseries.renderer.slug !==
          AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug
        ) {
          if (extent[0] < min) {
            min = extent[0];
          }
          if (extent[1] > max) {
            max = extent[1];
          }
        }
      }
      if (min > 0 && clampToZero) {
        min = 0;
      }
      if (max === Number.MIN_VALUE) {
        max = 0;
      }
      if (min === Number.MAX_VALUE) {
        min = 0;
      }
      return [min, max];
    }

    function getLegendIconWidth(renderer) {
      switch (renderer.slug) {
        case AnalyticAppRendererEnum.PLOT.slug:
          return 14;
        default:
          return 44;
      }
    }

    function getLegendSeries(series) {
      const legendSeries = [];
      let proximitySeries;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        if (
          timeseries.includeInLegend &&
          timeseries.interaction !== AnalyticAppInteractionEnum.PROXIMITY
        ) {
          legendSeries.push(timeseries);
        }
        if (timeseries.interaction === AnalyticAppInteractionEnum.PROXIMITY) {
          proximitySeries = timeseries;
        }
      }
      if (proximitySeries) {
        legendSeries.push(proximitySeries);
      }
      return legendSeries;
    }

    function getMargins(params) {
      const marginWidth = 36;
      const margins = {
        bottom: marginWidth,
        left: marginWidth,
        right: 0,
        top: 0,
      };

      // If there's more than one unit type
      // We'll require a second vertical
      // Axis, and thus a right margin:
      const numUnits = getNumUnits(params.units);
      if (numUnits > 1) {
        margins.right = marginWidth;
      }
      return margins;
    }

    function getNumColumnSeries(params) {
      const series = params.series;
      let numColumnSeries = 0;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        const renderer = timeseries.renderer;
        switch (renderer.slug) {
          case AnalyticAppRendererEnum.CLUSTERED_COLUMN.slug:
          case AnalyticAppRendererEnum.COLUMN.slug:
            numColumnSeries++;
            break;
          default:
            break;
        }
      }
      return numColumnSeries;
    }

    function getNumReadings(values) {
      return values.length;
    }

    function getNumUnits(units) {
      let numUnits = 0;
      for (const propName in units) {
        if (propName) {
          numUnits++;
        }
      }
      return numUnits;
    }

    function getPadding(params) {
      // Padding here is the number of pixels
      // By which to pad the chart content
      // Inside its container. For example,
      // We don't want the chart to use the
      // Full height of the content area
      // Because then it will collide with the
      // Legend. Top padding accounts for this.
      // Left and right padding is for columns.
      const left = 28;
      let right = 0;
      if (
        params.period.slug === ReportingPeriodEnum.MORE_THAN_TWELVE_MONTHS.slug
      ) {
        right = left;
      }
      return {
        bottom: 0,
        left,
        right,
        top: 60,
      };
    }

    function getPeriodLabel(series) {
      const format = DateTimeFormat.TEXTUAL;
      if (series.start.date() === series.end.date()) {
        return formatDate(series.start, format);
      }
      return formatDateRange(series.start, series.end, format);
    }

    function getRangeX(params) {
      const padding = params.padding;
      const contentWidth = params.contentWidth - padding.right;
      const left = params.columnWidth * 0.5 + padding.left;
      const timeseries = params.mainSeries;
      if (timeseries) {
        switch (timeseries.renderer.slug) {
          case AnalyticAppRendererEnum.CLUSTERED_COLUMN.slug:
          case AnalyticAppRendererEnum.COLUMN.slug:
          case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
          case AnalyticAppRendererEnum.PLOT.slug:
            return [left, contentWidth];
          default:
            return [0, contentWidth];
        }
      }
    }

    function getRangeY(params) {
      return [params.contentHeight, params.padding.top];
    }

    function getScaleX(domainX, rangeX) {
      return scaleTime().domain(domainX).range(rangeX);
    }

    function getScaleY(domainY, rangeY) {
      return scaleLinear().domain(domainY).range(rangeY);
    }

    function getScaleXbyTicks(timeseries, element) {
      // Specify the chart element using params.selection[0][0]
      const endDomainX = d3.select(element).selectAll('.x .tick')[0].length;

      // If there are no x axis ticks, return false
      if (!endDomainX) {
        return false;
      }

      const domainX = [0, endDomainX];
      const {rangeX} = timeseries;
      return scaleLinear().domain(domainX).range(rangeX);
    }

    function getCorrespondingIndex(scale, mouse) {
      const endDomainX = d3.selectAll('.x .tick')[0].length;
      const correspondingDomainValue = scale.invert(mouse[0]);
      let correspondingIndex =
        correspondingDomainValue >= 0
          ? Math.round(correspondingDomainValue)
          : 0;
      if (correspondingIndex === endDomainX) {
        correspondingIndex--;
      }
      return correspondingIndex;
    }

    function getSeriesAttributes(params) {
      const period = params.period;
      const series = params.series;
      const units = params.units;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = getCompacted(series[ii]);
        const unit = timeseries.unit;
        const start = timeseries.start;
        const end = timeseries.end;
        const rangeX = params.rangeX;
        const rangeY = params.rangeY;
        const domainX = getDomainX(start, end, period, timeseries.values);
        const domainY = units[unit.displayName].domainY;
        const scaleX = getScaleX(domainX, rangeX);
        const scaleY = getScaleY(domainY, rangeY);
        series[ii] = _.extend(timeseries, {
          rangeX,
          rangeY,
          domainX,
          domainY,
          scaleX,
          scaleY,
        });
      }
      return getConformed(params, series);
    }

    function getSeriesBisector() {
      return bisector((datum) => {
        return datum.x;
      }).right;
    }

    function getSeriesPoints(series) {
      const node = series.path.node();
      const length = node.getTotalLength();
      const points = [];
      for (let ii = 0, nn = length; ii < nn; ii++) {
        points.push(node.getPointAtLength(ii));
      }
      return points;
    }

    function getStretchedSeries(params) {
      // Hack to get timeseries data to render
      // Across the full area of the chart.
      const period = params.period;
      const series = params.series;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        if (timeseries.stretch) {
          // Only area or line series can be stretched:
          let hasPermittedRenderer;
          switch (timeseries.renderer.slug) {
            case AnalyticAppRendererEnum.AREA.slug:
            case AnalyticAppRendererEnum.LINE.slug:
              hasPermittedRenderer = true;
              break;
            default:
              hasPermittedRenderer = false;
              break;
          }
          // Only complete periods can be stretched
          // (also: these are the only complete periods
          // Supported for the permitted renderers):
          let hasPermittedPeriod;
          switch (period.slug) {
            case ReportingPeriodEnum.YESTERDAY.slug:
            case ReportingPeriodEnum.LAST_TWENTY_FOUR_HOURS.slug:
            case ReportingPeriodEnum.LAST_FOURTY_EIGHT_HOURS.slug:
            case ReportingPeriodEnum.LESS_THAN_FOURTY_EIGHT_HOURS.slug:
            case ReportingPeriodEnum.LESS_THAN_SEVEN_DAYS.slug:
              hasPermittedPeriod = true;
              break;
            default:
              hasPermittedPeriod = app === AnalyticAppEnum.LOAD_PROFILE;
              break;
          }
          // If both renderer and period check out, stretch:
          if (hasPermittedRenderer && hasPermittedPeriod) {
            const values = timeseries.values;
            const length = values.length;
            const last = values[length - 1];
            const clone = _.clone(last);
            const start = timeseries.start;
            let end = getEndOfPeriod(start, period);
            if (!end) {
              end = timeseries.end;
            }
            clone.timestamp = end;
            values.push(clone);
          }
        }
      }
      return series;
    }

    function getIndexOfLongestSeries(params) {
      const {series} = params;
      const lengths = series.map((ser) => ser.values.length);
      const longest = Math.max.apply(null, lengths);
      const longestIndex = lengths.indexOf(longest);
      return longestIndex;
    }

    function getSvgDivId(selection) {
      return `${d3_lucid.id.SVG_DIV}_${selection.attr('id')}`;
    }

    function getSvgId(selection) {
      return `${d3_lucid.id.SVG}_${selection.attr('id')}`;
    }

    function getTooltipOffset(params, timeseries) {
      const columnWidth = params.columnWidth;
      const left = params.margins.left;
      const columnOffset = left - columnWidth * 0.5;
      switch (timeseries.renderer.slug) {
        case AnalyticAppRendererEnum.CLUSTERED_COLUMN.slug:
        case AnalyticAppRendererEnum.COLUMN.slug:
        case AnalyticAppRendererEnum.DIFFERENCE_COLUMN.slug:
        case AnalyticAppRendererEnum.PLOT.slug:
          return columnOffset;
        default:
          return left;
      }
    }

    function getUnits(params) {
      const series = params.series;
      const units = {};
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        const unit = timeseries.unit;
        if (!units.hasOwnProperty(unit.displayName)) {
          units[unit.displayName] = {
            domainY: [],
            index: ii,
            series: [],
            type: unit,
          };
        }
        units[unit.displayName].series.push(timeseries);
      }
      for (const propName in units) {
        const unitGroup = units[propName];
        unitGroup.domainY = getDomainY(unitGroup.series);
      }
      return units;
    }

    function isProximate(params, point, mouse) {
      const series = params.series;
      const points = [];
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const timeseries = series[ii];
        if (timeseries.bisect) {
          const xIndex = timeseries.bisect(timeseries.points, mouse[0]);
          points.push(timeseries.points[xIndex]);
        }
      }
      const closestValueY = getClosest(points, mouse[1], 'y');
      if (closestValueY === point.y) {
        return true;
      }
      return false;
    }

    function playTransitions(params) {
      const series = params.series;
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        series[ii].transition();
      }
    }

    function reenableArrowButtons(params) {
      const arrowButtonsID = getID(d3_lucid.id.ARROW_BUTTONS);
      const arrowButtons = $(arrowButtonsID);
      if (arrowButtons) {
        const fadeRectID = getID(d3_lucid.id.FADE_RECT);
        arrowButtons.insertAfter(fadeRectID);
        arrowButtons.css('pointer-events', 'auto');
        const series = params.mainSeries;
        const period = params.period;
        const labelID = getID(d3_lucid.id.ARROW_BUTTONS_LABEL);
        const label = d3.select(labelID);
        const labelText = '';
        if (series && period) {
          getArrowButtonsLabel(series, period);
        }
        label.text(labelText);
      }
    }

    function scroll(params, direction) {
      let posX = params.contentWidth;
      switch (direction.slug) {
        case FORWARD:
          posX = -params.contentWidth;
          break;
        default:
          break;
      }
      params.content
        .transition()
        .attr('transform', `translate(${posX},0)`)
        .duration(SHORT)
        .ease(d3.ease('quad-in-out'))
        .each('end', function () {
          d3.select(this).remove();
          params.dispatcher.trigger('scroll', {
            direction,
            type: 'scroll',
          });
        });
      params.svg.style('cursor', 'default').style('pointer-events', 'none');
      params.xAxisTooltip.style('opacity', 0);
      transition_fadeRect();
    }

    function transition_fadeRect() {
      const selection = d3_lucid.svg.selectByID(d3_lucid.id.FADE_RECT);
      selection.transition().duration(SHORT).attr('fill-opacity', 0.6);
    }

    function updateLegendReadout(series, point) {
      let message = '';
      if (point) {
        const value = series.scaleY.invert(point.y);
        message = formatNumber(value);
        if (series.unit.id === currencyUnit.id) {
          message = `$${message}`;
        } else {
          message = [message, series.unit.shortName].join(' ');
        }
      }
      if (series.legendReadout) {
        series.legendReadout.text(message);
      }
    }

    function updateMarker(params, series, point, mouse) {
      const marker = series.marker;
      let display = 'none';
      if (!point) {
        return marker.attr('display', display);
      }
      switch (series.interaction) {
        case AnalyticAppInteractionEnum.PROXIMITY:
          const proximate = isProximate(params, point, mouse);
          if (proximate) {
            marker.attr('cx', point.x);
            marker.attr('cy', point.y);
            display = 'inline';
            updateDynamicLegendItem(params, series, point);
          }
          break;
        default:
          const difference = point.x - mouse[0];
          if (difference <= 1) {
            marker.attr('cx', point.x);
            marker.attr('cy', point.y);
            display = 'inline';
          }
          break;
      }
      return marker.attr('display', display);
    }

    function updateDynamicLegendItem(params, series, point) {
      if (!params.dynamicLegendItem) {
        return;
      }
      if (point) {
        const value = series.scaleY.invert(point.y);
        let message = formatNumber(value);
        if (series.unit.id === currencyUnit.id) {
          message = `$${message}`;
        } else {
          message = [message, series.unit.shortName].join(' ');
        }
        params.dynamicLegendIcon.style('stroke', series.stroke);
        params.dynamicLegendReadout.style('fill', series.fill);
        params.dynamicLegendReadout.text(message);
        params.dynamicLegendDisplayName.text(series.displayName);
        params.dynamicLegendItem.style('display', 'inline');
        params.dynamicStartDate = series.start;
      } else {
        params.dynamicLegendItem.style('display', 'none');
      }
    }
  }

  // Properties

  let app = null;
  let dispatcher;
  let enableArrowButtons = false;
  let height = 526;
  let message = '';
  let page = 1;
  let period = ReportingPeriodEnum.TODAY;
  let selection;
  let series = [];
  let showCustomPeriodLabel = false;
  let state = d3_lucid.states.INIT;
  let width = 1170;

  // Getters and setters

  chart.app = function (value) {
    if (value === undefined) {
      return app;
    }
    app = value;
    return chart;
  };

  chart.dispatcher = function (value) {
    if (value === undefined) {
      return dispatcher;
    }
    dispatcher = value;
    return chart;
  };

  chart.enableArrowButtons = function (value) {
    if (value === undefined) {
      return enableArrowButtons;
    }
    enableArrowButtons = value;
    return chart;
  };

  chart.height = function (value) {
    if (value === undefined) {
      return height;
    }
    height = value;
    return chart;
  };

  chart.message = function (value) {
    if (value === undefined) {
      return message;
    }
    message = value;
    return chart;
  };

  chart.page = function (value) {
    if (value === undefined) {
      return page;
    }
    page = value;
    return chart;
  };

  chart.period = function (value) {
    if (value === undefined) {
      return period;
    }
    period = value;
    return chart;
  };

  chart.selection = function (value) {
    if (value === undefined) {
      return selection;
    }
    selection = d3.select(value);
    return chart;
  };

  chart.showCustomPeriodLabel = function (value) {
    if (value === undefined) {
      return showCustomPeriodLabel;
    }
    showCustomPeriodLabel = value;
    return chart;
  };

  chart.series = function (value) {
    if (value === undefined) {
      return series;
    }
    series = value;
    return chart;
  };

  chart.state = function (value) {
    if (value === undefined) {
      return state;
    }
    state = value;
    return chart;
  };

  chart.width = function (value) {
    if (value === undefined) {
      return width;
    }
    width = value;
    return chart;
  };

  return chart;
};

export default timeseriesChart;
